// Signing in with a Google or Microsoft account.
//
// The browser's part of the flow is deliberately thin: it keeps the OAuth
// state (and, for Google, the PKCE verifier) in sessionStorage, sends the code
// back to the server, and does what the server's answer says. Every decision —
// known identity, matching account, or no account at all — is made server-side.

export type SignInProvider = "google" | "microsoft";

/** Any way somebody can sign in; what "Last used" remembers. */
export type LoginMethod = SignInProvider | "password";

export interface ProviderPrefill {
	provider: SignInProvider;
	email: string;
	first_name: string;
	last_name: string;
}

export interface SignedInResponse {
	refresh: string;
	access: string;
	user: { id: number; username: string; email: string };
}

export interface RegistrationRequiredResponse {
	registration_required: true;
	signup_token: string;
	prefill: ProviderPrefill;
}

export type ProviderLoginResponse = SignedInResponse | RegistrationRequiredResponse;

export const isRegistrationRequired = (
	response: ProviderLoginResponse
): response is RegistrationRequiredResponse => "registration_required" in response;

export interface ProviderRegistrationRequest {
	signup_token: string;
	first_name: string;
	last_name: string;
	linkedin_url: string;
	company_id: number;
	phone_number: string;
}

const STATE_KEY = "provider_oauth_state";
const VERIFIER_KEY = "provider_oauth_verifier";
const SIGNUP_TOKEN_KEY = "provider_signup_token";
const SIGNUP_PREFILL_KEY = "provider_signup_prefill";

export const providerLabel = (provider: SignInProvider): string =>
	provider === "google" ? "Google" : "Microsoft";

export const providerRedirectUri = (provider: SignInProvider): string =>
	`${window.location.origin}/login/oauth/${provider}`;

const randomUrlSafe = (): string => {
	const bytes = new Uint8Array(32);
	window.crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

const codeChallengeFor = async (verifier: string): Promise<string> => {
	const digest = await window.crypto.subtle.digest(
		"SHA-256",
		new TextEncoder().encode(verifier)
	);
	return btoa(String.fromCharCode(...new Uint8Array(digest)))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
};

/**
 * Send the browser to the provider's consent screen.
 *
 * `withMail` is dropped on a retry when an organisation refuses the mailbox
 * permissions outright — sign-in itself never needed them.
 */
export const startProviderSignIn = async (
	provider: SignInProvider,
	withMail = true
): Promise<void> => {
	const state = randomUrlSafe();
	const verifier = randomUrlSafe();
	sessionStorage.setItem(STATE_KEY, state);
	sessionStorage.setItem(VERIFIER_KEY, verifier);

	const response = await fetch("/api/v2/auth/provider_authorize/", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			provider,
			redirect_uri: providerRedirectUri(provider),
			state,
			code_challenge: await codeChallengeFor(verifier),
			with_mail: withMail,
		}),
	});
	if (!response.ok) {
		throw new Error("Could not start sign-in with that provider.");
	}
	const data: { auth_url: string } = await response.json();
	window.location.assign(data.auth_url);
};

/** True when the provider sent the browser back with the state we issued. */
export const stateMatches = (state: string | null): boolean => {
	const expected = sessionStorage.getItem(STATE_KEY);
	return Boolean(state) && Boolean(expected) && state === expected;
};

export const completeProviderSignIn = async (
	provider: SignInProvider,
	code: string
): Promise<ProviderLoginResponse> => {
	const response = await fetch("/api/v2/auth/provider_login/", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			provider,
			code,
			code_verifier: sessionStorage.getItem(VERIFIER_KEY) ?? "",
			redirect_uri: providerRedirectUri(provider),
		}),
	});
	const data = await response.json();
	if (!response.ok) {
		throw new Error(data.error ?? "Could not complete sign-in.");
	}
	sessionStorage.removeItem(STATE_KEY);
	sessionStorage.removeItem(VERIFIER_KEY);
	return data;
};

export const submitProviderRegistration = async (
	body: ProviderRegistrationRequest
): Promise<void> => {
	const response = await fetch("/api/v2/auth/provider_register/", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error ?? "Could not complete registration.");
	}
	clearPendingSignup();
};

/**
 * Hold the sign-up handle in sessionStorage rather than the URL: it can mint an
 * account, so it has no business in browser history or a shared link.
 */
export const storePendingSignup = (token: string, prefill: ProviderPrefill): void => {
	sessionStorage.setItem(SIGNUP_TOKEN_KEY, token);
	sessionStorage.setItem(SIGNUP_PREFILL_KEY, JSON.stringify(prefill));
};

export const readPendingSignup = (): {
	token: string;
	prefill: ProviderPrefill;
} | null => {
	const token = sessionStorage.getItem(SIGNUP_TOKEN_KEY);
	const prefill = sessionStorage.getItem(SIGNUP_PREFILL_KEY);
	if (!token || !prefill) {
		return null;
	}
	return { token, prefill: JSON.parse(prefill) as ProviderPrefill };
};

export const clearPendingSignup = (): void => {
	sessionStorage.removeItem(SIGNUP_TOKEN_KEY);
	sessionStorage.removeItem(SIGNUP_PREFILL_KEY);
};
